import { z } from "zod";
import type { PointTransaction, TransactionType } from "@/models/point-transaction";
import type { Station } from "@/models/station";
import type { User } from "@/models/user";

// Transaction type constants
export const TRANSACTION_TYPES = {
  PURCHASE: "purchase",
  DIRECT_SPENDING: "direct_spending",
  REDEMPTION: "redemption",
  BONUS: "bonus",
  MANUAL_ADJUSTMENT: "manual_adjustment",
  REFUND: "refund",
  SIGNUP_BONUS: "signup_bonus",
  EXPIRATION: "expiration",
  REVERSAL: "reversal",
} as const;

export const TYPES_NEEDING_DIRECT_POINTS = [
  TRANSACTION_TYPES.BONUS,
  TRANSACTION_TYPES.MANUAL_ADJUSTMENT,
  TRANSACTION_TYPES.REFUND,
  TRANSACTION_TYPES.SIGNUP_BONUS,
  TRANSACTION_TYPES.EXPIRATION,
] as const;

// Zod schema for form validation
export const transactionFormSchema = z
  .object({
    user: z.custom<User | null>(),
    station: z.custom<Station | null>(),
    transaction_type: z.custom<TransactionType>(),
    purchase_value_cents: z.number(),
    points: z.number(),
    description: z.string().optional(),
    reversed_transaction_id: z.string().nullable().optional(),
    reversed_transaction: z.custom<PointTransaction | null>().optional(),
  })
  .superRefine((data, ctx) => {
    // Validate user has loyalty account
    if (!data.user?.loyalty_account) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Customer must have a loyalty account",
        path: ["user"],
      });
    }

    // Validate station is selected
    if (!data.station?.id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Station is required",
        path: ["station"],
      });
    }

    const { transaction_type, purchase_value_cents, points } = data;

    // Purchase: requires amount > 0
    if (transaction_type === TRANSACTION_TYPES.PURCHASE) {
      if (purchase_value_cents <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Amount must be greater than 0",
          path: ["purchase_value_cents"],
        });
      }
    }

    // Direct spending: requires amount > 0
    if (transaction_type === TRANSACTION_TYPES.DIRECT_SPENDING) {
      if (purchase_value_cents <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Amount must be greater than 0",
          path: ["purchase_value_cents"],
        });
      }
    }

    // Direct points types: requires points !== 0
    if (
      TYPES_NEEDING_DIRECT_POINTS.includes(
        transaction_type as (typeof TYPES_NEEDING_DIRECT_POINTS)[number],
      )
    ) {
      if (points === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Points must not be zero",
          path: ["points"],
        });
      }
    }

    // Reversal: requires a transaction to reverse
    if (transaction_type === TRANSACTION_TYPES.REVERSAL) {
      if (!data.reversed_transaction_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Transaction to reverse is required",
          path: ["reversed_transaction_id"],
        });
      }
    }
  });

export type TransactionFormData = z.infer<typeof transactionFormSchema>;
